import { Box } from "@mui/material"
import React, { useCallback, useContext, useMemo, useState } from "react"
import useApi from "modules/arcadeGames"
import { useActiveContentContext } from "contexts/ActiveContentContext"
import { useAuthContext } from "./AuthContext"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { useGlobalAlert } from "./GlobalAlertContext"
import useSound from "hooks/useSound"
import { gamesSounds } from "utils/sounds"
import StartGame from "pages/games/components/StartGame"
import { useStudentHub } from "./StudentHubContext"
import { getScreenId } from "common/functions"
import useGameNavigation from "hooks/games/useGameNavigation"
import useGameSession from "hooks/games/useGameSession"
import useGameAnswerFlow from "hooks/games/useGameAnswerFlow"

export const GamesContext = React.createContext()

export const useGamesContext = () => {
  const context = useContext(GamesContext)
  if (context === undefined) {
    throw new Error("useGamesContext must be used within a GamesProvider")
  }
  return context
}

const GamesProvider = ({ children, sx }) => {
  const { userData, audioSettings, accountType, selectedGroupData } =
    useAuthContext()
  const [currentGame, setCurrentGame] = useState({
    colors: ["#fff", "#fff"],
  })
  const [gameSessionData, setGameSessionData] = useState(null)
  const [exerciseContentIndex, setExerciseContentIndex] = useState(0)
  const [exercisesContent, setExercisesContent] = useState([])
  const [answers, setAnswers] = useState([])
  const [isLoadingGameContent, setIsLoadingGameContent] = useState(false)
  const [isGameFinished, setIsGameFinished] = useState(false)
  const [countdownCompleted, setCountdownCompleted] = useState(false)
  const [playerStats, setPlayerStats] = useState({
    place: 0,
    totalGamesPlayed: 0,
    totalPoints: 0,
  })
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { setActiveContent } = useActiveContentContext()
  const [currentAnswerTime, setCurrentAnswerTime] = useState(0)
  const {
    setIndividualGameSession,
    setGameSessionAnswer,
    setGameSessionStatus,
    setGameSessionNextContent,
    getGameSessionById,
    getCategories,
    getGames,
    retryIndividualGameSession,
  } = useApi()
  const { setSessionActivityLog } = useStudentHub()
  const { showAlert } = useGlobalAlert()
  const [playGoodAnswer] = useSound(gamesSounds.goodAnswer, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })
  const [playWrongAnswer] = useSound(gamesSounds.wrongAnswer, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })

  const { fetchGameById, isPlayingGame } = useGameNavigation({
    location,
    getCategories,
    getGames,
    exercisesContent,
    exerciseContentIndex,
    setCountdownCompleted,
    setActiveContent,
  })

  const setNextContent = useCallback(async () => {
    const nextIndex = exerciseContentIndex + 1
    if (nextIndex < exercisesContent.length) {
      setExerciseContentIndex((prevIndex) => prevIndex + 1)
    } else {
      const { gameSessionId, participants } = gameSessionData
      const participantId =
        participants?.find((p) => p.userId === userData?.userId)
          ?.gameSessionParticipantId || -1
      await setSessionActivityLog({
        gameSessionContentId: 0,
        viewId: getScreenId("games"),
      })
      await setGameSessionStatus({
        gameSessionId,
        gameSessionParticipantId: participantId,
        gameSessionStatusId: 4,
      })
      setTimeout(() => {
        setIsGameFinished(true)
      }, 1000)
    }
    setIsLoadingGameContent(false)
  }, [exerciseContentIndex, exercisesContent, gameSessionData, userData])

  const { formatOnlyAnswered, formatPlayerAnswers, sendAnswer } =
    useGameAnswerFlow({
      gameSessionData,
      currentGame,
      exercisesContent,
      exerciseContentIndex,
      setAnswers,
      setGameSessionData,
      setIsLoadingGameContent,
      setIsGameFinished,
      setNextContent,
      setGameSessionAnswer,
      setGameSessionStatus,
      setGameSessionNextContent,
      userData,
      currentAnswerTime,
      playGoodAnswer,
      playWrongAnswer,
    })

  const { loadSession, createGame, loadGameContent } = useGameSession({
    gameSessionData,
    setGameSessionData,
    exercisesContent,
    exerciseContentIndex,
    setExercisesContent,
    setExerciseContentIndex,
    setIsGameFinished,
    setAnswers,
    setCountdownCompleted,
    setIsLoadingGameContent,
    setCurrentGame,
    formatOnlyAnswered,
    fetchGameById,
    getGameSessionById,
    setIndividualGameSession,
    retryIndividualGameSession,
    userData,
    selectedGroupData,
    navigate,
    showAlert,
    setSearchParams,
    searchParams,
    location,
  })

  const gameAnswers = useMemo(() => {
    if (isGameFinished) {
      return formatPlayerAnswers(answers, exercisesContent)
    }
    return answers
  }, [isGameFinished, answers, exercisesContent, formatPlayerAnswers])

  const value = useMemo(
    () => ({
      game: currentGame,
      level: {},
      exercisesContent,
      gameSessionData,
      gameAnswers,
      exerciseContentIndex,
      isLoadingGameContent,
      isGameFinished,
      gameTime: accountType < 7 ? 30 : 20,
      countdownCompleted,
      playerStats,
      setGame: setCurrentGame,
      createGame,
      sendAnswer,
      loadGameSession: loadSession,
      setNextContent,
      loadGameContent,
      setCountdownCompleted,
      setPlayerStats,
      setIsLoadingGameContent,
      setCurrentAnswerTime,
    }),
    [
      currentGame,
      exercisesContent,
      gameSessionData,
      gameAnswers,
      userData?.userId,
      exerciseContentIndex,
      isLoadingGameContent,
      searchParams,
      isGameFinished,
      countdownCompleted,
      selectedGroupData?.groupId,
      playerStats,
      currentAnswerTime,
    ]
  )

  return (
    <GamesContext.Provider value={value}>
      <Box sx={sx} className={"games-context"}>
        {children}
        {isPlayingGame && !countdownCompleted && currentGame && (
          <StartGame
            game={currentGame}
            onComplete={() => setCountdownCompleted(true)}
          />
        )}
      </Box>
    </GamesContext.Provider>
  )
}

export default GamesProvider
